// src/components/accessibility/A11yChecker.tsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";

interface A11yIssue {
  id: string;
  impact: string;
  description: string;
  help: string;
  helpUrl: string;
  nodes: {
    html: string;
    target: string[];
  }[];
}

const IssueContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #222;
  color: white;
  max-height: 300px;
  overflow-y: auto;
  padding: 1rem;
  z-index: 9999;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
  border-top: 2px solid #f44336;
`;

const IssueHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;

  h2 {
    margin: 0;
    font-size: 1rem;
  }

  button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 1.5rem;
  }
`;

const IssueList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const IssueItem = styled.li<{ impact: string }>`
  margin-bottom: 1rem;
  padding: 0.5rem;
  border-left: 4px solid
    ${props => {
      switch (props.impact) {
        case "critical":
          return "#f44336";
        case "serious":
          return "#ff9800";
        case "moderate":
          return "#ffeb3b";
        default:
          return "#4caf50";
      }
    }};
  background-color: #333;
`;

const ToggleButton = styled.button`
  position: fixed;
  bottom: 10px;
  right: 10px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10000;
  font-weight: bold;
  font-size: 1.2rem;

  &:focus {
    outline: 2px solid white;
  }
`;

const A11yChecker: React.FC = () => {
  const [issues, setIssues] = useState<A11yIssue[]>([]);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" &&
      typeof window !== "undefined"
    ) {
      // Se react-axe está configurado, ele enviará mensagens para o console
      // Vamos apenas escutar eventos personalizados para capturar problemas

      // Criar handler para eventos de violações do axe
      const handleAxeResults = (event: any) => {
        if (event.detail && Array.isArray(event.detail.violations)) {
          setIssues(event.detail.violations);
        }
      };

      // Registra o evento apenas se o axe estiver disponível
      try {
        window.addEventListener(
          "axe-results",
          handleAxeResults as EventListener
        );
      } catch (err) {
        console.warn("Não foi possível registrar evento axe-results", err);
      }

      return () => {
        try {
          window.removeEventListener(
            "axe-results",
            handleAxeResults as EventListener
          );
        } catch (err) {
          // Ignorar erros ao remover listener
        }
      };
    }
  }, []);

  if (process.env.NODE_ENV === "production" || issues.length === 0) {
    return null;
  }

  return (
    <>
      {" "}
      <ToggleButton
        onClick={() => setShow(!show)}
        aria-label={
          show ? "Hide accessibility issues" : "Show accessibility issues"
        }
        aria-expanded={show}
      >
        A11y
      </ToggleButton>
      {show && (
        <IssueContainer>
          <IssueHeader>
            <h2>Accessibility Issues ({issues.length})</h2>
            <button
              onClick={() => setShow(false)}
              aria-label="Close accessibility panel"
            >
              ×
            </button>
          </IssueHeader>

          <IssueList>
            {issues.map(issue => (
              <IssueItem key={issue.id} impact={issue.impact}>
                <h3>{issue.help}</h3>
                <p>{issue.description}</p>{" "}
                <p>
                  <strong>Impact:</strong> {issue.impact}
                </p>
                <p>
                  <strong>Affected elements:</strong> {issue.nodes.length}
                </p>
                <p>
                  <a
                    href={issue.helpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#4fc3f7" }}
                  >
                    Learn how to fix
                  </a>
                </p>
              </IssueItem>
            ))}
          </IssueList>
        </IssueContainer>
      )}
    </>
  );
};

export default A11yChecker;
